import { gql } from "@apollo/client";
import { useInput } from "lib/hooks/use-input";
import {
  useGetJarAmountQuery,
  useWithdrawFromJarMutation,
} from "lib/graphql-generated";
import currency from "currency.js";
import { Button } from "components/buttons/button";
import { inputClassNames, labelClassNames } from "components/forms/input";

const GET_PIF_JAR = gql`
  query GetJarAmount {
    pif_donations_aggregate(
      where: {
        _or: [
          { order: { status: { _nin: [cancelled, draft, refunded] } } }
          { order_id: { _is_null: true } }
        ]
      }
    ) {
      aggregate {
        sum {
          amount
        }
      }
    }
  }
`;

const WITHDRAW_FROM_JAR = gql`
  mutation WithdrawFromJar($amount: Int) {
    insert_pif_donations_one(object: { amount: $amount }) {
      id
    }
  }
`;

export const PifJar = () => {
  const withdrawAmount = useInput<number>(0);
  const { data, loading, error, refetch } = useGetJarAmountQuery({
    pollInterval: 10000,
  });
  const [modifyJar] = useWithdrawFromJarMutation();

  const handleWithdrawFromJar = (e) => {
    e.preventDefault();
    if (
      window.confirm(
        `Are you sure you want to withdraw £${withdrawAmount.value}`
      )
    ) {
      modifyJar({
        variables: {
          amount: currency(withdrawAmount.value).intValue * -1,
        },
      }).then(() => {
        withdrawAmount.init(0);
        refetch();
      });
    }
  };

  const handleAddToJar = (e) => {
    e.preventDefault();
    if (
      window.confirm(`Are you sure you want to add £${withdrawAmount.value}`)
    ) {
      modifyJar({
        variables: {
          amount: currency(withdrawAmount.value).intValue,
        },
      }).then(() => {
        withdrawAmount.init(0);
        refetch();
      });
    }
  };

  return (
    <article className=' bg-white overflow-hidden shadow rounded-lg justify-between flex flex-col'>
      <header className='px-5 pt-5 flex justify-start'>
        <h2
          className={`${
            !!data && data.pif_donations_aggregate.aggregate.sum.amount < 0
              ? "bg-red-100 text-red-default"
              : !!data && data.pif_donations_aggregate.aggregate.sum.amount >= 0
              ? "bg-green-100 text-green-default"
              : "text-gray-600"
          } text-sm px-4 rounded-full header pt-1 mb-6`}>
          Pay It Forward Jar
        </h2>
      </header>
      <section className='px-5 flex items-center mb-6'>
        <svg
          className='h-6 w-6 flex-shrink-0 text-gray-400'
          xmlns='http://www.w3.org/2000/svg'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
          aria-hidden='true'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3'
          />
        </svg>
        <dl className='ml-5 w-0 flex-1'>
          <dt className='text-sm font-medium text-gray-500 truncate'>
            Account Balance
          </dt>
          <dd className='text-lg font-medium text-gray-900'>
            {data?.pif_donations_aggregate ? (
              currency(data.pif_donations_aggregate.aggregate.sum.amount, {
                fromCents: true,
                symbol: "£",
              }).format()
            ) : (
              <span className='bg-gray-200 pr-20 py-0.5 rounded animate-pulse'></span>
            )}
          </dd>
        </dl>
      </section>
      <form className='p-5 bg-blueGray-100 grid grid-cols-2 gap-x-5 mt-auto'>
        <label className={`${labelClassNames}`}>
          Amount
          <input
            type='number'
            className={inputClassNames}
            {...withdrawAmount.bind}
          />
        </label>
        <Button
          disabled={!(withdrawAmount.value > 0) || loading}
          onClick={handleWithdrawFromJar}
          buttonType='primary'
          buttonSize='sm'
          className='mt-auto w-full'>
          Withdraw
        </Button>
        <button
          className='text-left text-xs mr-auto underline hover:text-green-default mt-2'
          onClick={handleAddToJar}>
          Add
        </button>
      </form>
    </article>
  );
};
